import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import CustomSelect from "./common/CustomSelect";
import CustomTextField from "./common/CustomTextField";
import CustomButton from "./common/CustomButton";
import { COLORS } from "../assets/theme";

export interface ConditionValue {
  conditionType?: string;
  logicalOperator?: string;
  status?: string;
  value?: string;
}

interface ConditionSenderFieldsProps {
  type: "dropdown" | "text";
  conditionTypeOptions?: { label: string; value: string }[];
  valueOptions?: { label: string; value: string }[];
  conditionValues?: ConditionValue[];
  errors: Record<string, string>;
  handleConditionChange: (index: number, field: string, value: string) => void;
  handleAddCondition: (
    conditionType?: string,
    logicalOperator?: "AND" | "OR"
  ) => void;
  handleRemoveCondition: (index: number) => void;
  handleLogicalOperatorChange?: (index: number, operator: "AND" | "OR") => void;
}

const ConditionSenderFields: React.FC<ConditionSenderFieldsProps> = ({
  type,
  conditionTypeOptions = [],
  valueOptions = [],
  conditionValues = [],
  errors,
  handleConditionChange,
  handleAddCondition,
  handleRemoveCondition,
  handleLogicalOperatorChange,
}) => {
  const getErrorMessage = (fieldKey: string) => errors[fieldKey] || "";

  const handleAddWithLimit = (logicalOperator: "AND" | "OR") => {
    if ((conditionValues?.length || 0) < 2) {
      handleAddCondition(undefined, logicalOperator);
    } else {
      console.warn("Maximum of two conditions allowed.");
    }
  };


  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        sx={{
          fontWeight: "500",
          fontSize: "16px",
          color: "#000",
        }}
      >
        If Conditions
      </Typography>
      <Stack spacing={1}>
        {(conditionValues.length > 0 ? conditionValues : [{}]).map(
          (condition, index) => (
            <React.Fragment key={index}>
              {index > 0 && conditionValues.length > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: { xs: "4px 0", sm: "8px 0" },
                    margin: { xs: "2px 0", sm: "4px 0" },
                  }}
                >
                  <Typography
                    onClick={() => {
                      if (handleLogicalOperatorChange) {
                        const currentOperator =
                          condition.logicalOperator || "AND";
                        const newOperator =
                          currentOperator === "AND" ? "OR" : "AND";
                        handleLogicalOperatorChange(index, newOperator);
                      }
                    }}
                    sx={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#A39455",
                      backgroundColor: "#F5F5F5",
                      padding: "6px 16px",
                      borderRadius: "12px",
                      border: "2px solid #A39455",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      cursor: handleLogicalOperatorChange
                        ? "pointer"
                        : "default",
                      "&:hover": handleLogicalOperatorChange
                        ? {
                            backgroundColor: "#E5E5E5",
                            transform: "scale(1.05)",
                          }
                        : {},
                      transition: "all 0.2s ease",
                    }}
                  >
                    {condition.logicalOperator || "AND"}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: { xs: 1, sm: 0 },
                  "&:last-child": {
                    marginBottom: 0,
                  },
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    minWidth: { xs: "100%", sm: "120px" },
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    color: "#3C3C43",
                    backgroundColor: "#EDEDED",
                    borderRadius: { xs: "12px 12px 0 0", sm: "12px 0 0 12px" },
                    border: "1px solid #868585",
                    borderRight: { xs: "1px solid #868585", sm: "none" },
                    fontWeight: "500",
                  }}
                >
                  Sender ID
                </Typography>
                <CustomSelect
                  label=""
                  value={condition.conditionType || ""}
                  options={conditionTypeOptions}
                  onChange={(value) =>
                    handleConditionChange(
                      index,
                      "conditionType",
                      value as string
                    )
                  }
                  placeholder="Condition"
                  size="small"
                  error={
                    !!getErrorMessage(`conditionValues.${index}.conditionType`)
                  }
                  helperText={getErrorMessage(
                    `conditionValues.${index}.conditionType`
                  )}
                  sx={{
                    maxHeight: "39px",
                    minWidth: { xs: "100%", sm: "150px" },
                    backgroundColor: "#EDEDED",
                    borderRadius: { xs: 0, sm: 0 },
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                    },
                  }}
                />
                {type === "dropdown" ? (
                  <CustomSelect
                    label=""
                    value={condition.status || condition.value || ""}
                    options={valueOptions}
                    onChange={(value) => {
                      handleConditionChange(
                        index,
                        condition.status !== undefined ? "status" : "value",
                        value as string
                      );
                    }}
                    placeholder="Select Value"
                    size="small"
                    error={
                      !!getErrorMessage(
                        `conditionValues.${index}.${
                          condition.status !== undefined ? "status" : "value"
                        }`
                      )
                    }
                    sx={{
                      minWidth: { xs: "100%", sm: "180px" },
                      height: "39px",
                      borderRadius: {
                        xs: "0 0 18px 18px",
                        sm: "0 18px 18px 0",
                      },
                      backgroundColor: "#EDEDED",
                    }}
                    disabled={
                      condition.conditionType === "IS_DIGIT" ||
                      condition.conditionType === "IS_NOT_DIGIT"
                    }
                  />
                ) : (
                  <CustomTextField
                    type="text"
                    placeholder="Value"
                    value={condition.status || condition.value || ""}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      handleConditionChange(
                        index,
                        condition.status !== undefined ? "status" : "value",
                        inputValue
                      );
                    }}
                    error={
                      !!getErrorMessage(
                        `conditionValues.${index}.${
                          condition.status !== undefined ? "status" : "value"
                        }`
                      )
                    }
                    sx={{
                      minWidth: { xs: "100%", sm: "140px" },
                      borderRadius: {
                        xs: "0 0 15px 15px",
                        sm: "0 15px 15px 0",
                      },
                      backgroundColor: "#EDEDED",
                      "& .MuiOutlinedInput-root fieldset": {
                        borderLeft: { xs: "1px solid #868585", sm: "none" },
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      },
                    }}
                    disabled={
                      condition.conditionType === "IS_DIGIT" ||
                      condition.conditionType === "IS_NOT_DIGIT"
                    }
                  />
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 1.5, sm: "8px" },
                    marginLeft: { xs: 0, sm: "2px" },
                    width: { xs: "100%", sm: "auto" },
                    marginTop: { xs: 1, sm: 0 },
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: { xs: "center", sm: "flex-start" },
                  }}
                >
                  {index === conditionValues.length - 1 && index !== 1 ? (
                    <>
                      <CustomButton
                        bgColor="#A39455"
                        textColor="#FFFFFF"
                        borderRadius="8px"
                        onClick={() => handleAddWithLimit("AND")}
                        isIconOnly
                        sx={{
                          width: { xs: "100%", sm: "40px" },
                          height: { xs: "40px", sm: "40px" },
                          minWidth: { xs: "100%", sm: "40px" },
                          fontSize: { xs: "16px", sm: "14px" },
                        }}
                      >
                        AND
                      </CustomButton>
                      <CustomButton
                        bgColor="#A39455"
                        textColor="#FFFFFF"
                        borderRadius="8px"
                        onClick={() => handleAddWithLimit("OR")}
                        isIconOnly
                        sx={{
                          width: { xs: "100%", sm: "40px" },
                          height: { xs: "40px", sm: "40px" },
                          minWidth: { xs: "100%", sm: "40px" },
                          fontSize: { xs: "16px", sm: "14px" },
                        }}
                      >
                        OR
                      </CustomButton>
                    </>
                  ) : (
                    <CustomButton
                      bgColor={COLORS.GOLD}
                      textColor="#FFFFFF"
                      borderRadius="8px"
                      onClick={() => handleRemoveCondition(index)}
                      isIconOnly
                      sx={{
                        height: { xs: "40px", sm: "35px" },
                        minWidth: { xs: "100%", sm: "35px" },
                        marginLeft: { xs: 0, sm: "2px" },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <RemoveIcon fontSize="small" />
                    </CustomButton>
                  )}
                </Box>
              </Box>
            </React.Fragment>
          )
        )}
      </Stack>
    </Box>
  );
};

export default ConditionSenderFields;
